import { z } from 'zod';

/**
 * SMS de bienvenue — envoyé au prospect au moment où sa fiche est
 * effectivement livrée au partenaire (transition `Lead.status → delivered`,
 * cf. hopti-delivered-sync). Il annonce le rappel imminent et donne le
 * numéro miroir du callcenter.
 *
 * Le rendu vit ici (dans `shared`) et pas côté API pour que l'écran de
 * configuration puisse afficher **exactement** le texte qui partira, avec
 * le même compteur de segments — un template SMS se relit avant d'être
 * envoyé, pas après.
 */

/**
 * Rollout du SMS de bienvenue. Trois états plutôt qu'un booléen :
 *  - `off` (défaut) : rien n'est envoyé, rien n'est écrit.
 *  - `dry_run` : le texte est rendu et tracé (`SmsLog` en `pending` +
 *    `statusDetails: 'dry-run'`), mais **aucun appel provider** → permet de
 *    valider le rendu sur de vraies fiches en prod sans texter un prospect.
 *  - `on` : envoi réel.
 */
export const WelcomeSmsModeSchema = z.enum(['off', 'dry_run', 'on']);
export type WelcomeSmsMode = z.infer<typeof WelcomeSmsModeSchema>;

/**
 * Template par défaut. Placeholders :
 *  - `{prenom}` : prénom du vendeur qui a saisi la fiche
 *  - `{dateSaisie}` : date + heure de saisie, Europe/Paris (`30/07 à 14h32`)
 *  - `{verticale}` : libellé prospect de la verticale (`Verticale.smsLabel`,
 *    fallback sur `name`) — surtout PAS le nom interne brut, « conseiller
 *    expert Chien / Chat » ne s'envoie pas.
 *  - `{numeroMiroir}` : n° entrant dédié du callcenter (`DeliveryPartner.mirrorPhone`)
 *
 * Un bloc `[[ … ]]` est **supprimé** si un placeholder qu'il contient est
 * vide : tous les partenaires n'ont pas de numéro miroir, la 2e phrase
 * disparaît alors proprement au lieu de sortir un trou dans le message.
 *
 * Limite 320 chars = 2 segments GSM-7 (1 segment = 160). Les guillemets
 * typographiques « », l'apostrophe courbe ’ et les … basculent le SMS en
 * UCS-2 (70 chars/segment) : l'UI de configuration les signale.
 */
export const DEFAULT_WELCOME_TEMPLATE =
  'Suite à la visite de {prenom} le {dateSaisie}, nous vous confirmons votre rdv dans les prochaines minutes avec votre conseiller expert {verticale}.[[ Vous pouvez également le joindre dès maintenant au {numeroMiroir}.]]';

/** Valeurs injectables dans le template. Une valeur vide = placeholder absent. */
export interface WelcomeSmsVars {
  prenom: string;
  dateSaisie: string;
  verticale: string;
  numeroMiroir: string;
}

/**
 * Rendu générique d'un template SMS (bienvenue, préétablis du vendeur…).
 * Ordre des opérations : on résout les blocs optionnels `[[…]]` AVANT la
 * substitution globale, sinon un placeholder vide laisserait un bloc vide
 * impossible à distinguer d'un bloc légitimement vide. Puis : espaces
 * doublés repliés, espace orpheline avant une virgule ou un point retirée
 * (un bloc optionnel vide en laisse une). Celles avant « ! » et « : »
 * restent : typographie française.
 */
export function renderSmsTemplate(
  template: string,
  vars: Readonly<Record<string, string | undefined>>,
): string {
  const resolved = template.replace(/\[\[(.*?)\]\]/gs, (_all, block: string) => {
    const referenced = [...block.matchAll(/\{(\w+)\}/g)].map((m) => m[1] ?? '');
    const anyEmpty = referenced.some((key) => !(vars[key] ?? '').trim());
    return anyEmpty ? '' : block;
  });

  return resolved
    .replace(/\{(\w+)\}/g, (all, key: string) => {
      const value = vars[key];
      // Placeholder inconnu : on le laisse tel quel pour que l'admin voie
      // sa faute de frappe dans l'aperçu au lieu d'un texte tronqué.
      return value === undefined ? all : value.trim();
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ ([,.])/g, '$1')
    .trim();
}

/** Rend le template du SMS de bienvenue. */
export function renderWelcomeSms(template: string, vars: WelcomeSmsVars): string {
  return renderSmsTemplate(template, { ...vars });
}

/**
 * `30/07 à 14h32` en Europe/Paris. Sans l'année : la visite est récente
 * (le SMS part à la livraison), l'année alourdit pour rien et coûte des
 * caractères sur un message déjà à 2 segments.
 */
export function formatWelcomeSmsDate(at: Date, timeZone = 'Europe/Paris'): string {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')} à ${get('hour')}h${get('minute')}`;
}

// ── Comptage de segments ────────────────────────────────────

/**
 * Alphabet GSM-7 (3GPP 23.038). Un caractère hors de cet alphabet force
 * l'opérateur en UCS-2 : 70 chars par segment au lieu de 160 → un message
 * de 143 chars passe de 1 à 3 segments. D'où le contrôle explicite.
 */
// Piège classique : la table contient « Ç » mais PAS « ç » minuscule, ni les
// guillemets typographiques « », ni l'apostrophe courbe ’, ni les points de
// suspension … — tous très fréquents en copier-coller depuis un traitement
// de texte. D'où `nonGsm7Chars` pour prévenir l'admin avant l'envoi.
const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
/** Caractères GSM-7 « étendus » : comptent DOUBLE (escape + char). */
const GSM7_EXTENDED = '^{}\\[~]|€';

export interface SmsSegmentInfo {
  encoding: 'GSM-7' | 'UCS-2';
  /** Longueur facturée (les chars étendus GSM-7 comptent 2). */
  length: number;
  segments: number;
}

/** Encodage + longueur facturée + nombre de segments réellement facturés. */
export function countSmsSegments(text: string): SmsSegmentInfo {
  let billed = 0;
  for (const char of text) {
    if (GSM7_BASIC.includes(char)) billed += 1;
    else if (GSM7_EXTENDED.includes(char)) billed += 2;
    else {
      // Bascule UCS-2 : 70 chars pour 1 segment, 67 en concaténé (les 3
      // chars restants portent l'en-tête UDH de concaténation).
      const length = [...text].length;
      return {
        encoding: 'UCS-2',
        length,
        segments: length === 0 ? 0 : length <= 70 ? 1 : Math.ceil(length / 67),
      };
    }
  }
  return {
    encoding: 'GSM-7',
    length: billed,
    segments: billed === 0 ? 0 : billed <= 160 ? 1 : Math.ceil(billed / 153),
  };
}

/**
 * Caractères qui feraient basculer le message en UCS-2, dédupliqués.
 * Sert d'avertissement dans l'écran de configuration (« « » doublent le
 * coût du SMS ») — le plus fréquent étant le copier-coller de guillemets
 * typographiques depuis un doc Word.
 */
export function nonGsm7Chars(text: string): string[] {
  const out = new Set<string>();
  for (const char of text) {
    if (!GSM7_BASIC.includes(char) && !GSM7_EXTENDED.includes(char)) out.add(char);
  }
  return [...out];
}
